import {
  List,
  ListItemButton,
  listItemButtonClasses,
  Tab,
  tabClasses,
  Tabs,
  tabsClasses,
  Tooltip,
  MenuItem,
  MenuList,
  tooltipClasses,
  TabProps,
  TooltipProps,
  styled as MuiStyled,
  menuItemClasses,
} from '@mui/material';
import React, { ReactNode, SyntheticEvent, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { styled } from 'styled-components';

import { HGATE_INCLUDED, USE_THETRARIS_LOGO } from '../../../../shared/env';
import { useUserState } from '../../../../shared/hooks/accountHook';
import headphoneIcon from '../../assets/icons/icon_headphone.svg';
import homeIcon from '../../assets/icons/icon_home.svg';
import infoIcon from '../../assets/icons/icon_info.svg';
import peopleIcon from '../../assets/icons/icon_people.svg';
import uploadCloudIcon from '../../assets/icons/icon_upload_cloud.svg';
import xrayDownloadIconBlue from '../../assets/icons/xray_download_blue_thick.png';
import xrayDownloadIconGrey from '../../assets/icons/xray_download_grey_thick.png';
import xrayUploadIconBlue from '../../assets/icons/xray_upload_blue_thick.png';
import xrayUploadIconGrey from '../../assets/icons/xray_upload_grey_thick.png';
import xrayViewIconBlue from '../../assets/icons/xray_view_blue_thick.png';
import xrayViewIconGrey from '../../assets/icons/xray_view_grey_thick.png';
import { ColoredIcon } from '../../common/components/Icon';
import { WindowSize, WindowSizeContext } from '../../hooks/windowSizeHook';

const TabIconWrapper = styled.span`
  display: flex;
  justify-content: right;
  flex: 1;
`;

const ShrinkTabIconWrapper = styled(TabIconWrapper)`
  justify-content: center;
`;
const LabelWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: left;
  width: 70%;
`;

const TabIcon = styled.img`
  /* ArrowDropUpIcon 요거 따라함 */
  display: inline-block;
  width: 1em;
  height: 1em;
  flex-shrink: 0;
  line-height: 0;
  font-size: 1.5rem;
  user-select: none;
`;

const sxProps = {
  [`&.${tabClasses.root}`]: {
    minWidth: '60px',
    minHeight: '64px',
    padding: 0,
    fontSize: '14px',
  },
  [`&.${tabClasses.root}.${tabClasses.selected}`]: {
    color: 'var(--HScan_Light_Blue, #2e5be0)',
    fontWeight: 700,
  },
  [`&.${tabClasses.root} > .${tabClasses.iconWrapper}`]: {
    marginRight: '16px',
  },
};

// sidebar menu items
interface WebTabProps extends Omit<TabProps, 'icon' | 'label'> {
  label: string | ReactNode;
  icon?: ReactNode;
  value: string;
}
export const WebTab = (props: WebTabProps) => {
  const { label, icon, value: navigateTo, ...left } = props;
  return navigateTo === 'channelTalk' ? (
    <Tab
      sx={sxProps}
      icon={<TabIconWrapper>{icon}</TabIconWrapper>}
      iconPosition="start"
      label={<LabelWrapper>{label}</LabelWrapper>}
      onClick={() => ChannelIO('showMessenger')}
      {...left}
    />
  ) : navigateTo === 'manual' ? (
    <Tab
      sx={sxProps}
      icon={<TabIconWrapper>{icon}</TabIconWrapper>}
      iconPosition="start"
      label={<LabelWrapper>{label}</LabelWrapper>}
      onClick={() =>
        window.open(
          USE_THETRARIS_LOGO
            ? 'https://drive.google.com/drive/folders/15eRAAqLrjk5gkQ_V1wRazvUU8JK3vBaG'
            : 'https://drive.google.com/drive/u/0/folders/1I93q2NeUu_SXpUbdGTZCwy6kNWHbC7AL',
          '_blank',
        )
      }
      {...left}
    />
  ) : (
    <Tab
      sx={sxProps}
      icon={<TabIconWrapper>{icon}</TabIconWrapper>}
      iconPosition="start"
      label={<LabelWrapper>{label}</LabelWrapper>}
      value={navigateTo}
      {...left}
    />
  );
};

interface ShrinkTabProps extends WebTabProps {
  hasSubmenu: boolean;
}
export const ShrinkTab = (props: ShrinkTabProps) => {
  const { value: navigateTo, label, icon, hasSubmenu, ...left } = props;

  const navigate = useNavigate();

  const StyledTooltip = MuiStyled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#ffffff',
      color: '#5a6473',
      maxWidth: 200,
      fontSize: theme.typography.pxToRem(14),
      border: '1px solid #dadde9',
    },
    [`& .${menuItemClasses.root}:hover`]: {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
    [`& .${menuItemClasses.selected}`]: {
      color: 'var(--HScan_Light_Blue, #2e5be0)',
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
  }));

  return (
    <StyledTooltip
      title={label}
      placement={hasSubmenu ? 'right-start' : 'right'}>
      {navigateTo === 'channelTalk' ? (
        <Tab
          sx={sxProps}
          icon={<ShrinkTabIconWrapper>{icon}</ShrinkTabIconWrapper>}
          onClick={() => ChannelIO('showMessenger')}
          {...left}
        />
      ) : navigateTo === 'manual' ? (
        <Tab
          sx={sxProps}
          icon={<ShrinkTabIconWrapper>{icon}</ShrinkTabIconWrapper>}
          onClick={() =>
            window.open(
              USE_THETRARIS_LOGO
                ? 'https://drive.google.com/drive/folders/15eRAAqLrjk5gkQ_V1wRazvUU8JK3vBaG?usp=drive_link'
                : 'https://drive.google.com/drive/u/0/folders/1I93q2NeUu_SXpUbdGTZCwy6kNWHbC7AL',
              '_blank',
            )
          }
          {...left}
        />
      ) : (
        <Tab
          sx={sxProps}
          icon={<ShrinkTabIconWrapper>{icon}</ShrinkTabIconWrapper>}
          value={navigateTo}
          onClick={() => typeof label !== 'string' && navigate(navigateTo)}
          {...left}
        />
      )}
    </StyledTooltip>
  );
};

interface WebViewListMenuProps {
  menuList: TabItem[];
}
const WebViewListMenu = (props: WebViewListMenuProps) => {
  const { menuList } = props;

  const { t } = useTranslation('translation', { keyPrefix: 'layout' });

  const navigate = useNavigate();
  const currentPath = location.pathname;

  return (
    <List
      disablePadding
      sx={{
        paddingLeft: '30%',
      }}>
      {menuList.map(listItem => (
        <ListItemButton
          key={listItem.path}
          selected={currentPath === listItem.path}
          onClick={() => navigate(listItem.path)}
          sx={{
            height: '58px',
            fontSize: '14px',
            [`&.${listItemButtonClasses.selected}`]: {
              color: 'var(--HScan_Light_Blue, #2e5be0)',
            },
          }}>
          {typeof listItem.label === 'string' && t(listItem.label)}
        </ListItemButton>
      ))}
    </List>
  );
};

// sidebar: web, shrink
const SidebarContainer = styled.nav<{ $isShrink?: boolean }>`
  width: ${p => (p.$isShrink ? '60px' : '235px')};
  background: white;
  flex-shrink: 0;
`;

const TAB_ITEMS = [
  '/',
  '/images-list',
  '/request-images',
  '/send-images',
  '/upload-images',
  '/my-page',
  'channelTalk',
  'manual',
];
const MY_PAGES = [
  '/my-page',
  '/notifications',
  '/requested-images',
  '/uploaded-images',
  '/agreement',
];

interface TabItem {
  path: string;
  icon?: ReactNode;
  label: string | ReactNode;
  subMenu?: ReactNode;
  showOnlyIdentified?: boolean;
  showOnlyHgateIncluded?: boolean;
}

const myPageMenuList: TabItem[] = [
  {
    label: 'sidebar.tab.6',
    path: MY_PAGES[0],
  },
  {
    label: 'sidebar.tab.7',
    path: MY_PAGES[1],
  },
  {
    label: 'sidebar.tab.8',
    path: MY_PAGES[2],
    showOnlyIdentified: true,
    showOnlyHgateIncluded: true,
  },
  {
    label: 'sidebar.tab.9',
    path: MY_PAGES[3],
    showOnlyIdentified: true,
    showOnlyHgateIncluded: true,
  },
  {
    label: 'sidebar.tab.10',
    path: MY_PAGES[4],
  },
];
export const Sidebar = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'layout' });

  const currentPath = location.pathname;
  const selectedMyPage = MY_PAGES.includes(currentPath);

  const navigate = useNavigate();
  const [user] = useUserState();

  const onChangeTab = (e: SyntheticEvent, value: string) => {
    navigate(value);
  };

  const windowSize = useContext(WindowSizeContext);
  const isShrink = windowSize === WindowSize.SHRINK_MENU;
  const isMobile = windowSize === WindowSize.MOBILE;

  const filteredMyMenuItems = useMemo(
    () =>
      myPageMenuList
        .filter(item => {
          if (item.showOnlyIdentified) {
            return user?.identified ?? false;
          }
          return true;
        })
        .filter(item => {
          if (item.showOnlyHgateIncluded) {
            return HGATE_INCLUDED ?? false;
          }
          return true;
        }),
    [user?.identified],
  );

  const tabItems = useMemo(
    () =>
      [
        {
          path: TAB_ITEMS[0],
          icon: (
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={homeIcon}
              imageWidth="17px"
              color="#a3a6ab"
            />
          ),
          label: `${t('sidebar.tab.0')}`,
        },
        {
          path: TAB_ITEMS[1],
          icon: (
            <TabIcon
              src={
                currentPath === TAB_ITEMS[1]
                  ? xrayViewIconBlue
                  : xrayViewIconGrey
              }
            />
          ),
          label: `${t('sidebar.tab.1')}`,
        },
        {
          path: TAB_ITEMS[2],
          icon: (
            <TabIcon
              src={
                currentPath === TAB_ITEMS[2]
                  ? xrayDownloadIconBlue
                  : xrayDownloadIconGrey
              }
            />
          ),
          label: `${t('sidebar.tab.2')}`,
          showOnlyIdentified: true,
          showOnlyHgateIncluded: true,
        },
        {
          path: TAB_ITEMS[3],
          icon: (
            <TabIcon
              src={
                currentPath === TAB_ITEMS[3]
                  ? xrayUploadIconBlue
                  : xrayUploadIconGrey
              }
            />
          ),
          label: `${t('sidebar.tab.3')}`,
        },
        {
          path: TAB_ITEMS[4],
          icon: (
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={uploadCloudIcon}
              imageWidth="22px"
              color={
                currentPath === TAB_ITEMS[4]
                  ? 'var(--HScan_Light_Blue, #2e5be0)'
                  : '#a3a6ab'
              }
            />
          ),
          label: `${t('sidebar.tab.4')}`,
        },
        {
          path: TAB_ITEMS[5],
          icon: (
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={peopleIcon}
              imageWidth="16px"
              color={
                selectedMyPage ? 'var(--HScan_Light_Blue, #2e5be0)' : '#a3a6ab'
              }
            />
          ),
          label: isShrink ? (
            <MenuList>
              {filteredMyMenuItems?.map(listItem => (
                <MenuItem
                  key={listItem.path}
                  onClick={() => navigate(listItem.path)}
                  selected={currentPath === listItem.path}>
                  {typeof listItem.label === 'string' && t(listItem.label)}
                </MenuItem>
              ))}
            </MenuList>
          ) : (
            `${t('sidebar.tab.5')}`
          ),
          subMenu: selectedMyPage && (
            <WebViewListMenu
              key="filteredMyMenuItems"
              menuList={filteredMyMenuItems}
            />
          ),
        },
        {
          path: TAB_ITEMS[6],
          icon: (
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={headphoneIcon}
              imageWidth="18px"
              color="#a3a6ab"
            />
          ),
          label: `${t('sidebar.tab.11')}`,
        },
        {
          path: TAB_ITEMS[7],
          icon: (
            <ColoredIcon
              width="24px"
              height="24px"
              imageUrl={infoIcon}
              imageWidth="18px"
              color="#a3a6ab"
            />
          ),
          label: `${t('sidebar.tab.12')}`,
        },
      ]
        .filter(item => {
          if (item.showOnlyIdentified) {
            return user?.identified ?? false;
          }
          return true;
        })
        .filter(item => {
          if (item.showOnlyHgateIncluded) {
            return HGATE_INCLUDED ?? false;
          }
          return true;
        }),
    [
      t,
      currentPath,
      selectedMyPage,
      isShrink,
      filteredMyMenuItems,
      navigate,
      user?.identified,
    ],
  );

  return (
    <>
      {!isMobile && (
        <SidebarContainer $isShrink={isShrink}>
          <Tabs
            orientation="vertical"
            value={selectedMyPage ? MY_PAGES[0] : currentPath}
            aria-label="Vertical Navigator"
            onChange={onChangeTab}
            sx={{
              paddingTop: '33px',
              paddingBottom: '33px',

              [`.${tabsClasses.indicator}`]: {
                width: '6px',
                left: 0,
                right: 'none',
                backgroundColor: 'var(--HScan_Light_Blue, #2e5be0)',
              },
            }}>
            {tabItems.flatMap((item, index) =>
              isShrink ? (
                <ShrinkTab
                  key={index}
                  icon={item.icon}
                  label={item.label}
                  value={item.path}
                  hasSubmenu={item.subMenu !== undefined}
                />
              ) : (
                [
                  <WebTab
                    key={index}
                    icon={item.icon}
                    label={item.label}
                    value={item.path}
                  />,
                  item.subMenu,
                ]
              ),
            )}
          </Tabs>
        </SidebarContainer>
      )}
    </>
  );
};
